import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "aadhaarPart1",
    "aadhaarPart2",
    "aadhaarPart3",
    "otpInput", 
    "txnIdField", 
    "step1", 
    "step2", 
    "step3",
    "step4",
    "cardName",
    "cardNumber",
    "cardAddress",
    "errorMsg", 
    "successMsg",
    "syncBtn",
    "syncLoader",
    "mainTitle",
    "downloadForm",
    "downloadMobileInput",
    "downloadAddressInput",
    "cardImageContainer",
    "cardImage"
  ]

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  }

  autoFocus(event) {
    const target = event.target
    const maxLength = parseInt(target.getAttribute("maxlength"), 10)
    const currentLength = target.value.length
    
    if (currentLength >= maxLength && event.key !== 'Backspace') {
      if (target === this.aadhaarPart1Target) {
        this.aadhaarPart2Target.focus()
      } else if (target === this.aadhaarPart2Target) {
        this.aadhaarPart3Target.focus()
      }
    }
    
    if (currentLength === 0 && event.key === 'Backspace') {
      if (target === this.aadhaarPart3Target) {
        this.aadhaarPart2Target.focus()
      } else if (target === this.aadhaarPart2Target) {
        this.aadhaarPart1Target.focus()
      }
    }
  }

  async generateOtp(event) {
    event.preventDefault()
    
    const part1 = this.aadhaarPart1Target.value || ""
    const part2 = this.aadhaarPart2Target.value || ""
    const part3 = this.aadhaarPart3Target.value || ""
    const aadhaar = part1 + part2 + part3
    
    this.authFlow = 'create'
    this.clearMessages()

    if (aadhaar.length !== 12 || !/^\d+$/.test(aadhaar)) {
      this.showError("Please enter a valid 12-digit Aadhaar number")
      return
    }

    const btn = event.currentTarget
    const originalText = btn.innerHTML
    btn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Sending...`
    btn.disabled = true

    try {
      const response = await fetch('/abha/generate_aadhaar_otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ aadhaar_number: aadhaar })
      })

      const data = await response.json()

      if (response.ok) {
        this.txnIdFieldTarget.value = data.txnId
        this.step1Target.classList.add("d-none")
        this.step2Target.classList.remove("d-none")
        this.showSuccess("OTP sent to Aadhaar-registered mobile.")
      } else {
        this.showError(data.error || "Failed to generate OTP")
      }
    } catch (err) {
      this.showError("Network error occurred")
    } finally {
      btn.innerHTML = originalText
      btn.disabled = false
    }
  }

  async verifyOtpAndCreate(event) {
    event.preventDefault()
    const otp = this.otpInputTarget.value
    const txnId = this.txnIdFieldTarget.value
    
    this.clearMessages()
    
    if (!otp || otp.length < 6) {
      this.showError("Please enter the 6-digit OTP")
      return
    }

    const btn = event.currentTarget
    const originalText = btn.innerHTML
    btn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Verifying...`
    btn.disabled = true

    try {
      if (this.authFlow === 'download') {
        const verifyRes = await fetch('/abha/auth/confirm', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({ otp: otp, txnId: txnId })
        })
        
        const verifyData = await verifyRes.json()

        if (!verifyRes.ok) {
          this.showError(verifyData.error || "Invalid OTP")
          btn.innerHTML = originalText
          btn.disabled = false
          return
        }

        btn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Downloading...`
        
        const createRes = await fetch('/abha/download_card', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({ token: verifyData.token }) 
        })

        const createData = await createRes.json()

        if (createRes.ok) {
          this.step2Target.classList.add("d-none")
          if (this.hasStep4Target) this.step4Target.classList.remove("d-none")
          
          if (createData.base64 && this.hasCardImageTarget) {
            this.cardImageTarget.src = `data:image/png;base64,${createData.base64}`
            if (this.hasCardImageContainerTarget) this.cardImageContainerTarget.classList.remove("d-none")
          }
          
          this.showSuccess(createData.message || "Card successfully downloaded!")
        } else {
          this.showError(createData.error || "Failed to download card")
        }
        btn.innerHTML = originalText
        btn.disabled = false
        return
      }

      const verifyRes = await fetch('/abha/verify_aadhaar_otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ otp: otp, txnId: txnId })
      })

      const verifyData = await verifyRes.json()

      if (!verifyRes.ok) {
        this.showError(verifyData.error || "Invalid OTP")
        btn.innerHTML = originalText
        btn.disabled = false
        return
      }

      if (verifyData.name && this.hasStep3Target) {
        this.step2Target.classList.add("d-none")
        this.step3Target.classList.remove("d-none")
        
        if(this.hasCardNameTarget) this.cardNameTarget.textContent = verifyData.name
        if(this.hasCardNumberTarget) this.cardNumberTarget.textContent = verifyData.abha_number
        if(this.hasCardAddressTarget) this.cardAddressTarget.textContent = verifyData.abha_address
        
        this.showSuccess("ABHA ID successfully generated!")
      } else {
        this.showSuccess("ABHA Linked successfully! Reloading...")
        setTimeout(() => window.location.reload(), 1500)
      }
    } catch (err) {
      this.showError("Network error occurred")
      btn.innerHTML = originalText
      btn.disabled = false
    }
  }

  async syncRecords(event) {
    event.preventDefault()
    this.clearMessages()
    
    this.syncBtnTarget.classList.add("d-none")
    this.syncLoaderTarget.classList.remove("d-none")

    try {
      const response = await fetch('/abha/request_consent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        }
      })

      const data = await response.json()

      if (response.ok) {
        this.showSuccess("Consent request sent! Please check the ABHA App on your phone to approve the request.")
      } else {
        this.showError(data.error || "Failed to initiate sync")
        this.syncBtnTarget.classList.remove("d-none")
        this.syncLoaderTarget.classList.add("d-none")
      }
    } catch (err) {
      this.showError("Network error occurred")
      this.syncBtnTarget.classList.remove("d-none")
      this.syncLoaderTarget.classList.add("d-none")
    }
  }

  toggleDownloadPage(event) {
    event.preventDefault()
    this.clearMessages()
    this.step1Target.classList.add("d-none")
    this.downloadFormTarget.classList.remove("d-none")
    if (this.hasMainTitleTarget) this.mainTitleTarget.textContent = "Download Existing ABHA Card"
  }

  toggleCreatePage(event) {
    event.preventDefault()
    this.clearMessages()
    this.downloadFormTarget.classList.add("d-none")
    this.step1Target.classList.remove("d-none")
    if (this.hasMainTitleTarget) this.mainTitleTarget.textContent = "Create Ayushman Bharat Health Account"
  }

  toggleDownloadInput(event) {
    const value = event.target.value
    if (value === "mobile") {
      this.downloadMobileInputTarget.classList.remove("d-none")
      this.downloadAddressInputTarget.classList.add("d-none")
    } else {
      this.downloadMobileInputTarget.classList.add("d-none")
      this.downloadAddressInputTarget.classList.remove("d-none")
    }
  }

  async downloadAction(event) {
    event.preventDefault()
    this.clearMessages()

    const downloadMode = document.querySelector('input[name="downloadOption"]:checked').value
    let healthId = ""
    let authMethod = "MOBILE_OTP"

    if (downloadMode === "mobile") {
      healthId = this.downloadMobileInputTarget.querySelector('input').value
      if (!healthId || healthId.length !== 10) {
        this.showError("Please enter a valid 10-digit mobile number")
        return
      }
    } else {
      healthId = this.downloadAddressInputTarget.querySelector('input').value
      if (!healthId) {
        this.showError("Please enter a valid ABHA address")
        return
      }
    }

    const btn = event.currentTarget
    const originalText = btn.innerHTML
    btn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Sending OTP...`
    btn.disabled = true

    this.authFlow = 'download'

    try {
      const response = await fetch('/abha/auth/init', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({ health_id: healthId, auth_method: authMethod })
      })

      const data = await response.json()

      if (response.ok) {
        this.txnIdFieldTarget.value = data.txnId
        this.downloadFormTarget.classList.add("d-none")
        this.step2Target.classList.remove("d-none")
        this.showSuccess("OTP sent successfully to the registered mobile.")
      } else {
        this.showError(data.error || "Failed to initiate download")
      }
    } catch (err) {
      this.showError("Network error occurred")
    } finally {
      btn.innerHTML = originalText
      btn.disabled = false
    }
  }

  showError(msg) {
    this.errorMsgTarget.textContent = msg
    this.errorMsgTarget.classList.remove("d-none")
  }

  showSuccess(msg) {
    this.successMsgTarget.textContent = msg
    this.successMsgTarget.classList.remove("d-none")
  }

  clearMessages() {
    this.errorMsgTarget.classList.add("d-none")
    this.successMsgTarget.classList.add("d-none")
    this.errorMsgTarget.textContent = ""
    this.successMsgTarget.textContent = ""
  }
}